import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { listDoctors } from '../../api/doctors.js'
import { useDebounce } from '../../hooks/useDebounce.js'

// Combined search + pick for the DoctorProfile directory — used on the
// referral form to route a referral to a *specific* Wantok clinician
// (populates Referral.to_doctor). This is the switch that turns a referral
// from a printed letter into a real hand-off: once to_doctor is set, the
// receiving clinician sees the referral in their queue and gains access to
// the patient's record + prior encounters (see backend core/access.py::
// doctor_authorized_patient_ids — a doctor is authorised for any patient
// they are the to_doctor on a Referral for).
//
// Design mirrors FacilityAutocomplete:
//  - On first focus the dropdown shows the directory (optionally narrowed by
//    user_type). No "type 2+ characters" gate — the doctor roster is small.
//  - Typing debounces 200ms and re-queries with ?search=, narrowing live.
//  - Once a clinician is picked we swap the input for a "selected" pill card
//    the user can clear with ×.
//
// Props:
//   userType (string)   - '' | 'doctor' | 'specialist' — filters the roster
//   value    (id|null)  - currently selected doctor id
//   selected (object)   - full selected DoctorProfile row (for display)
//   onPick({...})       - called with the full doctor row on pick
//   onClear()           - called when user clicks × on the pill
const USER_TYPE_LABEL = {
  doctor: 'Doctor',
  specialist: 'Specialist',
}

function DoctorMeta({ doctor }) {
  const bits = [doctor.specialty, doctor.place_of_work].filter(Boolean)
  if (bits.length === 0) return null
  return (
    <div className="text-xs text-slate-500 mt-0.5">{bits.join(' · ')}</div>
  )
}

export default function DoctorAutocomplete({
  userType = '',
  value,
  selected,
  onPick,
  onClear,
}) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)
  const debounced = useDebounce(q, 200)

  const { data = [], isFetching } = useQuery({
    queryKey: ['doctors-autocomplete', userType, debounced],
    queryFn: () =>
      listDoctors({ userType: userType || undefined, search: debounced || undefined }),
    staleTime: 5 * 60_000,
    // Always fetch — empty q still lists the roster. Skip once a doctor is
    // picked (the pill view doesn't need the list).
    enabled: !value,
  })

  // Reset highlight when the data set changes so "Enter" never picks a stale row.
  useEffect(() => {
    setHighlight(0)
  }, [debounced, userType])

  function pick(item) {
    onPick?.(item)
    setQ('')
    setOpen(false)
    setHighlight(0)
  }

  function onKeyDown(e) {
    if (!open) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((i) => Math.min(i + 1, data.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (data[highlight]) pick(data[highlight])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  // ---- Selected pill view -------------------------------------------------
  if (value && selected) {
    return (
      <div className="border border-wantok-200 bg-wantok-50 rounded-md p-3 text-sm">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-1.5 py-0.5 bg-white text-wantok-700 border border-wantok-200 rounded text-[10px] uppercase">
                {USER_TYPE_LABEL[selected.user_type] || selected.user_type}
              </span>
              <span className="font-semibold text-slate-800">
                {selected.full_name || selected.username}
              </span>
              {selected.specialty && (
                <span className="text-slate-600">· {selected.specialty}</span>
              )}
            </div>
            {selected.place_of_work && (
              <div className="text-xs text-slate-600 mt-0.5">
                {selected.place_of_work}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClear}
            className="text-slate-400 hover:text-rose-600 text-lg leading-none"
            aria-label="Change clinician"
          >
            ×
          </button>
        </div>
      </div>
    )
  }

  // ---- Search + list view -------------------------------------------------
  const showMenu = open || (!!q && data.length > 0)
  return (
    <div className="relative">
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder="Search clinicians by name, specialty, or place of work…"
        className="w-full border rounded px-3 py-2 text-sm"
        inputMode="search"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showMenu}
      />
      {showMenu && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-auto text-sm">
          {isFetching && data.length === 0 && (
            <div className="p-2 text-slate-400">Searching…</div>
          )}
          {!isFetching && data.length === 0 && (
            <div className="p-2 text-slate-400">
              No clinicians match. Try clearing the filter or searching differently.
            </div>
          )}
          {data.map((item, i) => (
            <button
              type="button"
              key={item.id}
              onMouseDown={(e) => {
                e.preventDefault()
                pick(item)
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`block w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 ${
                i === highlight ? 'bg-wantok-50' : 'hover:bg-slate-50'
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-1.5 py-0.5 bg-wantok-50 text-wantok-700 border border-wantok-100 rounded text-[10px] uppercase">
                  {USER_TYPE_LABEL[item.user_type] || item.user_type}
                </span>
                <span className="font-medium text-slate-800">
                  {item.full_name || item.username}
                </span>
                {item.specialty && (
                  <span className="text-slate-500 text-xs">· {item.specialty}</span>
                )}
              </div>
              <DoctorMeta doctor={item} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
